import { Spin, Empty, Typography, Table, Button, Modal, Form, Input, InputNumber, Switch } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined } from '@ant-design/icons';
import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useAxiosPost from '../../hooks/useAxiosPost';
import useMultiNotification from '../../hooks/useNotification';
import { formatDate } from '../../helpers/date';

interface CompanyTableDataType {
  key: number;
  intPkCompanyID: number;
  companyName: string;
  address: string;
  enable: number;
  createdAt: string;
  flatTariffRate: number;
  deviceLimit: number;
}

export default function MasterStaffHome() {
  const { openNotification, contextHolder } = useMultiNotification();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [postApiData, apiResponse, apiError, apiLoading, apiMessage, apiErrorCount] =
    useAxiosPost<ResponseProp>('companies/all');

  const [
    createCompanyData,
    createCompanyResponse,
    createCompanyError,
    createCompanyLoading,
    createCompanyMessage,
    createCompanyErrorCount,
  ] = useAxiosPost<ResponseProp>('master-staff-auth/createCompanyWithAdmin');

  const [
    toggleStatusData,
    toggleStatusResponse,
    toggleStatusError,
    toggleStatusLoading,
    toggleStatusMessage,
    toggleStatusErrorCount,
  ] = useAxiosPost<ResponseProp>('companies/toggle-status');

  const [allCompanies, setAllCompanies] = useState<CompanyTableDataType[]>([]);
  const [togglingCompanyId, setTogglingCompanyId] = useState<number | null>(null);
  const [togglingEnableValue, setTogglingEnableValue] = useState<number | null>(null);
  const [switchStates, setSwitchStates] = useState<Record<number, boolean>>({});
  const postApiDataRef = useRef(postApiData);
  const lastProcessedErrorCount = useRef({ api: 0, create: 0, toggle: 0 });
  const hasProcessedCreateSuccess = useRef(false);
  const lastProcessedToggleResponse = useRef<any>(null);

  // Keep ref updated
  useEffect(() => {
    postApiDataRef.current = postApiData;
  }, [postApiData]);

  useEffect(() => {
    postApiDataRef.current({});
  }, []);

  useEffect(() => {
    if (apiResponse !== null) {
      const responseData: any = apiResponse.responseData;
      if (Array.isArray(responseData)) {
        // Transform data for table with keys
        const companiesWithKeys = responseData.map((company: any) => ({
          ...company,
          key: company.intPkCompanyID,
        }));
        setAllCompanies(companiesWithKeys);
        // Reset switch states to match current data
        const newSwitchStates: Record<number, boolean> = {};
        companiesWithKeys.forEach((company) => {
          newSwitchStates[company.intPkCompanyID] = company.enable === 1;
        });
        setSwitchStates(newSwitchStates);
      } else {
        setAllCompanies([]);
        setSwitchStates({});
      }
    }
  }, [apiResponse]);

  useEffect(() => {
    if (apiError !== null && apiErrorCount !== 0 && lastProcessedErrorCount.current.api !== apiErrorCount) {
      lastProcessedErrorCount.current.api = apiErrorCount;
      openNotification('error', 'Failed to Load Companies', apiError);
    }
  }, [apiError, apiErrorCount, openNotification]);

  // Handle create company success - only process once
  useEffect(() => {
    if (createCompanyResponse !== null && !hasProcessedCreateSuccess.current) {
      hasProcessedCreateSuccess.current = true;
      openNotification('success', 'Company Created', 'Company and admin created successfully');
      setIsModalOpen(false);
      form.resetFields();
      // Refresh companies list
      setTimeout(() => {
        postApiDataRef.current({});
      }, 100);
    }
  }, [createCompanyResponse, form, openNotification]);

  // Reset the flag when modal opens or createCompanyResponse becomes null
  useEffect(() => {
    if (createCompanyResponse === null) {
      hasProcessedCreateSuccess.current = false;
    }
  }, [createCompanyResponse]);

  // Handle create company error
  useEffect(() => {
    if (
      createCompanyError !== null &&
      createCompanyErrorCount !== 0 &&
      lastProcessedErrorCount.current.create !== createCompanyErrorCount
    ) {
      lastProcessedErrorCount.current.create = createCompanyErrorCount;
      openNotification('error', 'Failed to Create Company', createCompanyError);
    }
  }, [createCompanyError, createCompanyErrorCount, openNotification]);

  // Handle toggle status success
  useEffect(() => {
    if (toggleStatusResponse !== null && toggleStatusResponse !== lastProcessedToggleResponse.current && togglingCompanyId !== null) {
      lastProcessedToggleResponse.current = toggleStatusResponse;
      openNotification('success', 'Status Updated', toggleStatusResponse.message || 'Company status updated successfully');
      // Reset state immediately
      setTogglingCompanyId(null);
      setTogglingEnableValue(null);
      // Refresh companies list from API to get accurate state
      postApiDataRef.current({});
    }
  }, [toggleStatusResponse, togglingCompanyId, openNotification]);

  // Handle toggle status error
  useEffect(() => {
    if (
      toggleStatusError !== null &&
      toggleStatusErrorCount !== 0 &&
      lastProcessedErrorCount.current.toggle !== toggleStatusErrorCount
    ) {
      lastProcessedErrorCount.current.toggle = toggleStatusErrorCount;
      openNotification('error', 'Failed to Update Status', toggleStatusError);
      setTogglingCompanyId(null);
      setTogglingEnableValue(null);
      // Reset the last processed response on error so we can retry
      lastProcessedToggleResponse.current = null;
    }
  }, [toggleStatusError, toggleStatusErrorCount, openNotification]);

  const handleRowClick = (record: CompanyTableDataType, event: React.MouseEvent) => {
    // Don't navigate if clicking on the switch
    if ((event.target as HTMLElement).closest('.ant-switch')) {
      return;
    }
    navigate(`/company/${record.intPkCompanyID}`);
  };

  const handleToggleStatus = (record: CompanyTableDataType, checked: boolean, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click
    const enableValue = checked ? 1 : 0;
    const action = enableValue === 1 ? 'enable' : 'disable';
    
    // Update switch state immediately for visual feedback
    setSwitchStates((prev) => ({
      ...prev,
      [record.intPkCompanyID]: checked,
    }));
    
    Modal.confirm({
      title: `${action === 'enable' ? 'Enable' : 'Disable'} Company`,
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to ${action} "${record.companyName}"?`,
      okText: 'Yes',
      cancelText: 'No',
      onOk: () => {
        setTogglingCompanyId(record.intPkCompanyID);
        setTogglingEnableValue(enableValue);
        toggleStatusData({
          intPkCompanyID: record.intPkCompanyID,
          enable: enableValue,
        });
      },
      onCancel: () => {
        // Revert switch state if user cancels
        setSwitchStates((prev) => ({
          ...prev,
          [record.intPkCompanyID]: record.enable === 1,
        }));
      },
    });
  };

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    if (!createCompanyLoading) {
      setIsModalOpen(false);
      form.resetFields();
    }
  };

  const handleCreate = (values: any) => {
    const { companyName, address, flatTariffRate, deviceLimit, adminName, adminEmail, adminPassword } = values;
    createCompanyData({
      companyName: companyName.trim(),
      address: address.trim(),
      flatTariffRate: Number(flatTariffRate),
      deviceLimit: Number(deviceLimit),
      adminName: adminName.trim(),
      adminEmail: adminEmail.trim().toLowerCase(),
      adminPassword: adminPassword,
    });
  };

  const columns: ColumnsType<CompanyTableDataType> = [
    {
      title: 'Code',
      dataIndex: 'intPkCompanyID',
      key: 'intPkCompanyID',
      render: (id: number) => id || '-',
    },
    {
      title: 'Company Name',
      dataIndex: 'companyName',
      key: 'companyName',
      render: (text: string) => <Typography.Text strong>{text}</Typography.Text>,
    },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
      render: (text: string) => text || '-',
    },
    {
      title: 'Flat Tariff Rate',
      dataIndex: 'flatTariffRate',
      key: 'flatTariffRate',
      render: (rate: number) => rate || 0,
    },
    {
      title: 'Device Limit',
      dataIndex: 'deviceLimit',
      key: 'deviceLimit',
      render: (limit: number) => limit || 0,
    },
    {
      title: 'Status',
      dataIndex: 'enable',
      key: 'enable',
      render: (enable: number, record: CompanyTableDataType) => {
        // Use switchStates if available, otherwise fall back to enable prop
        const switchChecked = switchStates[record.intPkCompanyID] !== undefined 
          ? switchStates[record.intPkCompanyID] 
          : enable === 1;
        
        return (
          <Switch
            checked={switchChecked}
            onChange={(checked, e) => {
              e.stopPropagation();
              handleToggleStatus(record, checked, e);
            }}
            loading={togglingCompanyId === record.intPkCompanyID}
            disabled={togglingCompanyId === record.intPkCompanyID}
            checkedChildren="Active"
            unCheckedChildren="Inactive"
            onClick={(checked, e) => e.stopPropagation()}
          />
        );
      },
    },
    {
      title: 'Date Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => (date ? formatDate(date) : '-'),
    },
  ];

  return (
    <>
      {contextHolder}
      <div style={{ padding: '24px' }}>
        <Typography.Title level={2}>Companies</Typography.Title>
        <Typography.Paragraph style={{ marginBottom: '24px' }}>Manage all companies in the system</Typography.Paragraph>

        <div
          style={{
            marginBottom: '16px',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Button type="primary" icon={<PlusOutlined />} onClick={showModal} size="large">
            Create Company
          </Button>
        </div>

        {apiLoading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
            <Typography style={{ marginTop: '16px', display: 'block' }}>Loading companies...</Typography>
          </div>
        ) : apiError && !apiLoading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Empty
              description={<Typography.Text type="danger">{apiError || 'Failed to load companies'}</Typography.Text>}
            />
          </div>
        ) : allCompanies.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Empty description="No companies found." />
          </div>
        ) : (
          <div className="table-container-custom">
            <Table
              className="antd-table-striped companies-table-clickable"
              columns={columns}
              dataSource={allCompanies}
              pagination={false}
              loading={apiLoading}
              onRow={(record) => ({
                onClick: (event) => handleRowClick(record, event),
                style: { cursor: 'pointer' },
              })}
            />
          </div>
        )}

        {/* Create Company Modal */}
        <Modal
          title="Create New Company"
          open={isModalOpen}
          onCancel={handleModalClose}
          footer={null}
          width={600}
          maskClosable={!createCompanyLoading}
          closable={!createCompanyLoading}
        >
          <Form form={form} layout="vertical" onFinish={handleCreate} requiredMark={false}>
            <Form.Item
              label={<span style={{ fontWeight: 500 }}>Company Name</span>}
              name="companyName"
              rules={[
                {
                  required: true,
                  message: 'Please enter company name',
                },
                {
                  min: 1,
                  message: 'Company name cannot be empty',
                },
                {
                  max: 45,
                  message: 'Company name must be less than 45 characters',
                },
                {
                  whitespace: true,
                  message: 'Company name cannot be only spaces',
                },
              ]}
            >
              <Input placeholder="Enter company name" disabled={createCompanyLoading} maxLength={45} showCount />
            </Form.Item>

            <Form.Item
              label={<span style={{ fontWeight: 500 }}>Address</span>}
              name="address"
              rules={[
                {
                  required: true,
                  message: 'Please enter address',
                },
                {
                  min: 1,
                  message: 'Address cannot be empty',
                },
                {
                  max: 300,
                  message: 'Address must be less than 300 characters',
                },
                {
                  whitespace: true,
                  message: 'Address cannot be only spaces',
                },
              ]}
            >
              <Input.TextArea
                placeholder="Enter address"
                disabled={createCompanyLoading}
                maxLength={300}
                showCount
                rows={3}
              />
            </Form.Item>

            <Form.Item
              label={<span style={{ fontWeight: 500 }}>Flat Tariff Rate</span>}
              name="flatTariffRate"
              rules={[
                {
                  required: true,
                  message: 'Please enter flat tariff rate',
                },
                {
                  type: 'number',
                  min: 0,
                  message: 'Flat tariff rate must be 0 or greater',
                },
              ]}
            >
              <InputNumber
                placeholder="Enter flat tariff rate"
                disabled={createCompanyLoading}
                style={{ width: '100%' }}
                min={0}
              />
            </Form.Item>

            <Form.Item
              label={<span style={{ fontWeight: 500 }}>Device Limit</span>}
              name="deviceLimit"
              rules={[
                {
                  required: true,
                  message: 'Please enter device limit',
                },
                {
                  type: 'number',
                  min: 0,
                  message: 'Device limit must be 0 or greater',
                },
              ]}
            >
              <InputNumber
                placeholder="Enter device limit"
                disabled={createCompanyLoading}
                style={{ width: '100%' }}
                min={0}
              />
            </Form.Item>

            <Typography.Title level={5} style={{ marginTop: '16px', marginBottom: '16px' }}>
              Admin Details
            </Typography.Title>

            <Form.Item
              label={<span style={{ fontWeight: 500 }}>Admin Name</span>}
              name="adminName"
              rules={[
                {
                  required: true,
                  message: 'Please enter admin name',
                },
                {
                  min: 1,
                  message: 'Admin name cannot be empty',
                },
                {
                  max: 100,
                  message: 'Admin name must be less than 100 characters',
                },
                {
                  whitespace: true,
                  message: 'Admin name cannot be only spaces',
                },
              ]}
            >
              <Input placeholder="Enter admin name" disabled={createCompanyLoading} maxLength={100} showCount />
            </Form.Item>

            <Form.Item
              label={<span style={{ fontWeight: 500 }}>Admin Email</span>}
              name="adminEmail"
              rules={[
                {
                  required: true,
                  message: 'Please enter admin email',
                },
                {
                  type: 'email',
                  message: 'Please enter a valid email',
                },
                {
                  max: 45,
                  message: 'Admin email must be less than 45 characters',
                },
              ]}
            >
              <Input placeholder="Enter admin email" disabled={createCompanyLoading} type="email" maxLength={45} />
            </Form.Item>

            <Form.Item
              label={<span style={{ fontWeight: 500 }}>Admin Password</span>}
              name="adminPassword"
              rules={[
                {
                  required: true,
                  message: 'Please enter admin password',
                },
                {
                  min: 6,
                  message: 'Password must be at least 6 characters long',
                },
                {
                  max: 256,
                  message: 'Password must be less than 256 characters',
                },
              ]}
            >
              <Input.Password placeholder="Enter admin password" disabled={createCompanyLoading} maxLength={256} />
            </Form.Item>

            <Form.Item
              label={<span style={{ fontWeight: 500 }}>Confirm Password</span>}
              name="confirmPassword"
              dependencies={['adminPassword']}
              rules={[
                {
                  required: true,
                  message: 'Please confirm the password',
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('adminPassword') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('The two passwords do not match!'));
                  },
                }),
              ]}
            >
              <Input.Password placeholder="Confirm admin password" disabled={createCompanyLoading} maxLength={256} />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, marginTop: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <Button onClick={handleModalClose} disabled={createCompanyLoading}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit" loading={createCompanyLoading}>
                  Create Company
                </Button>
              </div>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </>
  );
}
